import { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { WelletContext } from '../../../context/WelletContext';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import { Spinner } from '../../../components/Common/Spinner/Spinner';
import ErrorBox from '../../../components/Common/ErrorBox/ErrorBox';
import styles from './LoginMethodSelector.module.css';
import EmailIcon from '../../../components/icons/EmailIcon';
import LockIconLogin from '../../../components/icons/LockIcon';
import ArrowIconSimple from '../../../components/icons/ArrowIconSimple';
import WhatsAppIcon from '../../../components/icons/WhatsAppIcon';
import { allowedMethodsService } from '../../../services/allowedMethods/allowedMethodsService';
import { sendCodeVerificationService } from '../../../services/whatsappCode/whatsappCodeService';

const LoginMethodSelector = ({ history }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const welletContext = useContext(WelletContext);

  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [error, setError] = useState(undefined);
  const [methods, setMethods] = useState([]);
  const [whatsappValue, setWhatsappValue] = useState(null);
  const app = useSelector((state) => state.app);
  const tenantId = useSelector((state) => state.app.tenantId);
  const email = new URLSearchParams(location.search).get('email');

  useEffect(() => {
    const fetchAllowedMethods = async () => {
      try {
        const res = await allowedMethodsService(welletContext, email, tenantId);
        const allowed = res?.data || [];

        setMethods(allowed.map((m) => m.method));

        const whatsappMethod = allowed.find((m) => m.method === 'WHATSAPP');
        if (whatsappMethod) setWhatsappValue(whatsappMethod.value);
      } catch (err) {
        console.error(err);
        setError(t('callApiError'));
      }
    };

    fetchAllowedMethods();
  }, []);

  const handleEmailLogin = async () => {
    setSendingEmail(true);
    setError(undefined);
    try {
      await welletContext.apis.tickets.post(`/users/login-one-time`, {
        email,
        tenantId,
        language: app.language,
      });

      history.push({
        pathname: '/login-code',
        search: `?email=${email}`,
      });
    } catch (error) {
      console.error(error);
      setError(t('callApiError'));
    } finally {
      setSendingEmail(false);
    }
  };

  const handleWhatsAppLogin = async () => {
    setSendingWhatsApp(true);
    setError(undefined);
    try {
      await sendCodeVerificationService(welletContext, email, tenantId, app.language);
      history.push({
        pathname: '/sign-up-phone-validation-login',
        search: `?email=${email}&phone=${encodeURIComponent(whatsappValue)}`,
      });
    } catch (error) {
      console.error(error);
      setError(t('callApiError'));
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handlePasswordLogin = () => {
    history.push({
      pathname: '/login-password',
      search: `?email=${email}`,
    });
  };

  const isLoading = methods.length === 0 && !error;

  return (
    <>
      <Header title={''} hasBackground={true} />
      <ContentApp background={true}>
        <div>
          <div className='font-medium bold' style={{ color: '#fff' }}>
            {t('login.selectMethod')}
          </div>
          <div className='text-secondary font-tiny mt-1'>{t('login.selectMethodSubtitle')}</div>

          {isLoading ? (
            <div className='d-flex justify-content-center mt-5'>
              <Spinner size={30} color='#1d9bf0' />
            </div>
          ) : (
            <div className={styles.methodsContainer} style={{ '--count': methods.length }}>
              {methods.includes('EMAIL') && (
                <div
                  className={styles.methodCard}
                  onClick={!sendingEmail ? handleEmailLogin : null}
                  style={{ '--index': 0 }}
                >
                  <div className={styles.iconContainer}>
                    <EmailIcon color='#1d9bf0' />
                  </div>
                  <div className={styles.methodContent}>
                    <div className='fw-semibold font-small mb-1'>{t('login.emailCode')}</div>
                    <div className='text-medium-gray font-extra-small'>
                      {t('login.emailCodeDesc')}
                    </div>
                  </div>
                  {sendingEmail ? (
                    <Spinner size={20} color='#1d9bf0' />
                  ) : (
                    <ArrowIconSimple color={'var(--color-deep-gray)'} />
                  )}
                </div>
              )}

              {methods.includes('PASSWORD') && (
                <div
                  className={styles.methodCard}
                  onClick={handlePasswordLogin}
                  style={{ '--index': 1 }}
                >
                  <div className={styles.iconContainer}>
                    <LockIconLogin color='#1d9bf0' size='24px' />
                  </div>
                  <div className={styles.methodContent}>
                    <div className='fw-semibold font-small mb-1'>{t('login.password')}</div>
                    <div className='text-medium-gray font-extra-small'>
                      {t('login.passwordDesc')}
                    </div>
                  </div>
                  <ArrowIconSimple color={'var(--color-deep-gray)'} />
                </div>
              )}

              {methods.includes('WHATSAPP') && (
                <div
                  className={styles.methodCard}
                  onClick={!sendingWhatsApp ? handleWhatsAppLogin : null}
                  style={{ '--index': 2 }}
                >
                  <div className={styles.iconContainer}>
                    <WhatsAppIcon color='#1d9bf0' size='24px' />
                  </div>
                  <div className={styles.methodContent}>
                    <div className='fw-semibold font-small mb-1'>{t('login.whatsapp')}</div>
                    <div className='text-medium-gray font-extra-small'>
                      {whatsappValue
                        ? `${t('login.whatsappDesc')} (${whatsappValue})`
                        : t('login.whatsappDesc')}
                    </div>
                  </div>
                  {sendingWhatsApp ? (
                    <Spinner size={20} color='#1d9bf0' />
                  ) : (
                    <ArrowIconSimple color={'var(--color-deep-gray)'} />
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className='mt-3'>
              <ErrorBox title={error} />
            </div>
          )}
        </div>
      </ContentApp>
    </>
  );
};

export default LoginMethodSelector;
